import { identity, withoutEmptyProps } from 'ovpn-ui-toolkit';

export const NoValidate = Symbol('preventValidation');

export type ValidatorFn = (...args: any[]) => string | null | undefined | typeof NoValidate;

/**
 * Composes single-argument validator functions from left to right.

 * @param {...Function} funcs The functions to compose.
 * @returns {Function} A function obtained by composing the argument functions
 * from left to right. For example, compose(f, g, h) is identical to doing
 * (...args) => f(...args) || g(...args) || h(...args)
 */
export function compose(..._funcs: (ValidatorFn | ValidatorFn[])[]) {
    const funcs: any[] = [];

    // basically an Array.flatMap - but we don't want to include polyfills
    for (const fn of _funcs) {
        if (Array.isArray(fn)) {
            funcs.push.apply(funcs, fn);
        } else {
            funcs.push(fn);
        }
    }

    if (funcs.length === 0) {
        return identity;
    }

    if (funcs.length === 1) {
        return funcs[0];
    }

    return (...args: any[]) => {
        for (const fn of funcs) {
            const aResult = fn(...args);
            if (aResult === NoValidate) {
                break;
            }
            if (aResult) {
                return aResult;
            }
        }
        return null;
    };
}

export function validateIf<Args extends any[]>(condition: (...args: Args) => boolean) {
    return (...args: Args) => (condition(...args) ? null : NoValidate);
}

export function createValidator(validatorObject: Record<string, ValidatorFn | ValidatorFn[]>) {
    return (values: any, props: any) => {
        const errors = {} as Record<string, ReturnType<ValidatorFn>>;
        for (const [key, value] of Object.entries(validatorObject)) {
            if (key === '_error') {
                errors[key] = compose(value)(values, props);
            } else {
                const arg = values[key];
                errors[key] = compose(value)(arg, values, props);
            }
        }
        return errors;
    };
}

export function nestedValidator(validatorObject: Record<string, ValidatorFn>) {
    const validator = createValidator(validatorObject);
    return (values: any, _: any, props: any) => validator(values, props);
}

export const arrayValidator =
    (itemValidator: ValidatorFn) =>
    (values: any, ...args: any[]) => {
        if (Array.isArray(values)) {
            return values.map((t, index) => itemValidator(t, ...args, index));
        }
        return undefined;
    };

/**
 * Creates React-Hook-From compatible validation schema from our validation schema
 */
export const schemaResolver = (schema: any) => (values: any, context: any) => {
    const errors = schema(values, context);

    return {
        values,
        errors: errors && typeof errors === 'object' ? withoutEmptyProps(errors, true) : {},
    };
};
